import { DataType } from '../types/data_type';
import { utils } from '../utils/utils';
/**
 * Contains internatialization functionality.
 */
export var i18n;
(function (i18n) {
    var englishUSLocaleSettings = {
        shortDateFormat: 'MM/dd/yyyy',
        longDateFormat: 'dd MMM, yyyy',
        editDateFormat: 'MM/dd/yyyy',
        shortTimeFormat: 'HH:mm',
        editTimeFormat: 'HH:mm',
        longTimeFormat: 'HH:mm:ss',
        shortMonthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        longMonthNames: ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'],
        shortWeekDayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        longWeekDayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        decimalSeparator: '.',
        currency: 'USD'
    };
    var defaultLocale = {
        localeId: 'en-US',
        englishName: 'English',
        displayName: 'English',
        texts: {
            ButtonOK: 'OK',
            ButtonCancel: 'Cancel',
            Yes: 'Yes',
            No: 'No',
            True: 'True',
            False: 'False'
        },
        settings: englishUSLocaleSettings
    };
    var allLocales = {
        'en-US': defaultLocale
    };
    var currentLocale;
    var mappers = [];
    function mapInfo(info) {
        for (var _i = 0, mappers_1 = mappers; _i < mappers_1.length; _i++) {
            var mapper = mappers_1[_i];
            mapper(info);
        }
    }
    function addMapper(mapper) {
        mappers.push(mapper);
    }
    i18n.addMapper = addMapper;
    /**
     * Gets added locales with their names.
     * @returns  The locales.
     */
    function getLocales() {
        var result = [];
        for (var locale in allLocales) {
            result.push({
                locale: locale,
                englishName: allLocales[locale].englishName,
                displayName: allLocales[locale].displayName
            });
        }
        return result.sort(function (a, b) {
            if (a.englishName > b.englishName) {
                return 1;
            }
            else if (a.englishName === b.englishName) {
                return 0;
            }
            return -1;
        });
    }
    i18n.getLocales = getLocales;
    /**
     * Gets the current locale ID.
     * @returns The locale.
     */
    function getCurrentLocale() {
        return currentLocale.localeId;
    }
    i18n.getCurrentLocale = getCurrentLocale;
    /**
    * Sets the curent locale.
    * @deprecated Use setCurrentLocale instead
    * @param l The locale.
    */
    function setLocale(l) {
        console.warn('This method is deprecated. Use setCurrentLocale instead');
        setCurrentLocale(l);
    }
    i18n.setLocale = setLocale;
    /**
     * Sets the curent locale.
     * @param localeId The locale.
     */
    function setCurrentLocale(localeId) {
        var newLocale = allLocales[localeId];
        if (newLocale) {
            utils.assignDeep(currentLocale, newLocale);
        }
        else {
            currentLocale.englishName = localeId;
            currentLocale.displayName = localeId;
            currentLocale.texts = utils.assignDeep({}, defaultLocale.texts);
        }
        currentLocale.localeId = localeId;
    }
    i18n.setCurrentLocale = setCurrentLocale;
    /**
     * Returns localized text by the key defined in parameter.
     * Here we get the text of the resource string assigned to CmdClickToAddCondition key:
     *
     ```
       const text = i18n.getText('CmdClickToAddCondition')
     ```
     * @param args The keys of the resource string.
     * @returns Text of the resource defined by key or null if the key is not found
     *
     */
    function getText() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var textsObj = currentLocale.texts;
        var resText = '';
        if (args && args.length) {
            var argLength = args.length;
            for (var i = 0; i < argLength; i++) {
                resText = textsObj[args[i]];
                if (typeof resText === 'object') {
                    textsObj = resText;
                }
                else {
                    break;
                }
            }
        }
        return resText;
    }
    i18n.getText = getText;
    function getLocaleSettings() {
        return currentLocale.settings;
    }
    i18n.getLocaleSettings = getLocaleSettings;
    function getOneLocaleSetting(key) {
        return currentLocale.settings[key];
    }
    i18n.getOneLocaleSetting = getOneLocaleSetting;
    function getShortMonthName(monthNum) {
        var settings = getLocaleSettings();
        if (monthNum > 0 && monthNum < 13) {
            return settings.shortMonthNames[monthNum - 1];
        }
        else {
            throw 'Wrong month number: ' + monthNum;
        }
    }
    i18n.getShortMonthName = getShortMonthName;
    function getLongMonthName(monthNum) {
        var settings = getLocaleSettings();
        if (monthNum > 0 && monthNum < 13) {
            return settings.longMonthNames[monthNum - 1];
        }
        else {
            throw 'Wrong month number: ' + monthNum;
        }
    }
    i18n.getLongMonthName = getLongMonthName;
    function getShortWeekDayName(dayNum) {
        var settings = getLocaleSettings();
        if (dayNum > 0 && dayNum < 8) {
            return settings.shortWeekDayNames.length >= dayNum
                ? settings.shortWeekDayNames[dayNum - 1]
                : dayNum.toString();
        }
        else {
            throw 'Wrong month number: ' + dayNum;
        }
    }
    i18n.getShortWeekDayName = getShortWeekDayName;
    function getLongWeekDayName(dayNum) {
        var settings = getLocaleSettings();
        if (dayNum > 0 && dayNum < 8) {
            return settings.longWeekDayNames.length >= dayNum
                ? settings.longWeekDayNames[dayNum - 1]
                : dayNum.toString();
        }
        else {
            throw 'Wrong month number: ' + dayNum;
        }
    }
    i18n.getLongWeekDayName = getLongWeekDayName;
    /**
     * Updates the locale settings (date/time formats, separators, etc) for the specified locale.
     * @param settingsToUpdate a LocaleSettings object
     */
    function updateLocaleSettings(settingsToUpdate) {
        if (!currentLocale.settings) {
            currentLocale.settings = utils.assignDeep({}, englishUSLocaleSettings);
        }
        currentLocale.settings = utils.assignDeep(currentLocale.settings, settingsToUpdate);
    }
    i18n.updateLocaleSettings = updateLocaleSettings;
    /**
     * Updates the texts for the current locale
     * @param texts A plain JS object that contains textual resources
     */
    function updateLocaleTexts(texts) {
        if (typeof texts !== 'object') {
            console.error('Wrong parameter type in updateLocaleTexts function call.' +
                'The first parameter (localeId) is not necessary. Use updateLocaleTexts(texts) instead');
            return;
        }
        mapInfo({ localeId: currentLocale.localeId, texts: texts });
        utils.assignDeep(currentLocale.texts, texts);
    }
    i18n.updateLocaleTexts = updateLocaleTexts;
    function updateDefaultTexts(texts) {
        for (var localeId in allLocales) {
            var locale = allLocales[localeId];
            locale.texts = utils.assignDeep({}, texts, locale.texts);
        }
        currentLocale.texts = utils.assignDeep({}, texts, currentLocale.texts);
    }
    i18n.updateDefaultTexts = updateDefaultTexts;
    /**
     * Updates the information for the specified locale.
     * @param localeId The locale ID (like 'en', 'de', 'uk', etc).
     * If the locale does exist yet - it will be added
     * @param localeInfo  a LocaleInfo object that contains the locale settings and textual resources
     */
    function updateLocaleInfo(localeId, localeData) {
        mapInfo(localeData);
        var localeInfoToUpdate = currentLocale;
        if (localeId) {
            if (!localeData.localeId) {
                localeData.localeId = localeId;
            }
            localeInfoToUpdate = allLocales[localeId];
            if (!localeInfoToUpdate) {
                localeInfoToUpdate = utils.assignDeep({}, defaultLocale);
                allLocales[localeId] = localeInfoToUpdate;
            }
        }
        utils.assignDeep(localeInfoToUpdate, localeData);
    }
    i18n.updateLocaleInfo = updateLocaleInfo;
    /**
     * Adds the locale.
     * @param localeId The locale ID (like 'en', 'de', 'uk', etc).
     * If the locale does exist yet - it will be created
     * @param localeInfo - a LocaleInfo object that contains the locale settings and textual resources
     */
    function addLocale(localeId, localeInfo) {
        updateLocaleInfo(localeId, localeInfo);
    }
    i18n.addLocale = addLocale;
    /**
     * Overwrites some locale settings (date/time formats) with the formats used in browser's current language
     */
    function determineSettingsByLocale(localeId) {
        var now = new Date(2020, 5, 7, 19, 34, 56, 88);
        var dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
        var timeOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric' };
        var dateStr = now.toLocaleDateString(localeId, dateOptions);
        var timeStr = now.toLocaleTimeString(localeId, timeOptions);
        var dateFormat = dateStr
            .replace('07', 'dd')
            .replace('7', 'd')
            .replace('06', 'MM')
            .replace('6', 'M')
            .replace('2020', 'yyyy')
            .replace('20', 'yy');
        var timeFormat = timeStr
            .replace('19', 'HH')
            .replace('07', 'hh')
            .replace('7', 'h')
            .replace('34', 'mm')
            .replace('56', 'ss')
            .replace('PM', 'tt');
        if (!currentLocale.settings) {
            currentLocale.settings = {};
        }
        var localeSettings = {
            shortDateFormat: dateFormat,
            shortTimeFormat: timeFormat
        };
        updateLocaleSettings(localeSettings);
    }
    function loadBrowserLocaleSettings() {
        var lang = typeof navigator === 'object' ? navigator.language : undefined;
        determineSettingsByLocale(lang);
    }
    function resetLocales() {
        if (!currentLocale) {
            currentLocale = utils.assignDeep({}, defaultLocale);
            loadBrowserLocaleSettings();
        }
    }
    i18n.resetLocales = resetLocales;
    function dateTimeToStr(dateTime, dataType, format) {
        if (format) {
            if (format == 'd') {
                format = buildShortDateTimeFormat(DataType.Date);
            }
            else if (format == 'D') {
                format = buildLongDateTimeFormat(DataType.Date);
            }
            else if (format == 'f') {
                format = buildShortDateTimeFormat(DataType.DateTime);
            }
            else if (format == 'F') {
                format = buildLongDateTimeFormat(DataType.DateTime);
            }
        }
        else {
            format = buildShortDateTimeFormat(dataType);
        }
        return utils.dateTimeToStr(dateTime, format);
    }
    i18n.dateTimeToStr = dateTimeToStr;
    function buildShortDateTimeFormat(dataType) {
        var localeSettings = getLocaleSettings();
        var format;
        switch (dataType) {
            case DataType.Date:
                format = localeSettings.shortDateFormat;
                break;
            case DataType.Time:
                format = localeSettings.shortTimeFormat;
                break;
            default:
                format = localeSettings.shortDateFormat + ' ' + localeSettings.shortTimeFormat;
                break;
        }
        return format;
    }
    function buildLongDateTimeFormat(dataType) {
        var localeSettings = getLocaleSettings();
        var format;
        switch (dataType) {
            case DataType.Date:
                format = localeSettings.longDateFormat;
                break;
            case DataType.Time:
                format = localeSettings.longTimeFormat;
                break;
            default:
                format = localeSettings.longDateFormat + ' ' + localeSettings.longTimeFormat;
                break;
        }
        return format;
    }
    function numberToStr(number, format) {
        if (format && format.length > 0) {
            var type = format.charAt(0).toUpperCase();
            if (type === 'S') {
                return formatWithSequence(number, format.slice(1));
            }
            else if (['D', 'F', 'C'].indexOf(type) >= 0) {
                var locale = getCurrentLocale();
                return number.toLocaleString(locale, getNumberFormatOptions(format));
            }
            else {
                return convertWithMask(Math.trunc(number), format);
            }
        }
        var localeSettings = getLocaleSettings();
        return utils.numberToStr(number, localeSettings.decimalSeparator);
    }
    i18n.numberToStr = numberToStr;
    function booleanToStr(bool, format) {
        if (format && format.length > 0) {
            var type = format.charAt(0).toUpperCase();
            if (type === 'S') {
                var values = format.slice(1).split('|');
                if (values.length > 1) {
                    var value = values[(bool) ? 1 : 0];
                    return i18n.getText(value) || value;
                }
            }
        }
        return "" + bool;
    }
    i18n.booleanToStr = booleanToStr;
    var cachedSequenceFormats = {};
    function formatWithSequence(number, format) {
        if (!cachedSequenceFormats[format]) {
            // parse and save in cache format values 
            var values_2 = format.split('|')
                .filter(function (v) { return v.length > 0; })
                .map(function (v) { return v.split('='); });
            cachedSequenceFormats[format] = {};
            if (values_2.length > 0) {
                if (values_2[0].length > 1) {
                    for (var _i = 0, values_1 = values_2; _i < values_1.length; _i++) {
                        var value = values_1[_i];
                        cachedSequenceFormats[format][Number.parseInt(value[1])] = value[0];
                    }
                }
                else {
                    values_2.forEach(function (value, index) {
                        cachedSequenceFormats[format][index] = value[0];
                    });
                }
            }
        }
        var values = cachedSequenceFormats[format];
        if (values[number] !== undefined) {
            var value = values[number];
            return i18n.getText(value) || value;
        }
        return number.toString();
    }
    function convertWithMask(number, mask) {
        var value = number.toString();
        var result = '';
        var index = value.length - 1;
        for (var i = mask.length - 1; i >= 0; i--) {
            var ch = mask.charAt(i);
            if (ch === '#' || ch === '0') {
                if (index >= 0) {
                    result += value.charAt(index);
                    index--;
                }
                else {
                    if (ch === '0') {
                        result += 0;
                    }
                }
            }
            else {
                result += ch;
            }
        }
        return result.split('').reverse().join('');
    }
    function getNumberFormatOptions(format) {
        var localeSettings = getLocaleSettings();
        var type = format[0].toUpperCase();
        var digits = (format.length > 1)
            ? Number.parseInt(format.slice(1))
            : type == 'D' ? 1 : 2;
        switch (type) {
            case 'D':
                return {
                    style: 'decimal',
                    useGrouping: false,
                    minimumIntegerDigits: digits
                };
            case 'C':
                return {
                    style: 'currency',
                    currency: localeSettings.currency,
                    minimumFractionDigits: digits
                };
            default:
                return {
                    style: 'decimal',
                    minimumFractionDigits: digits,
                    maximumFractionDigits: digits
                };
        }
    }
})(i18n || (i18n = {}));
//# sourceMappingURL=i18n.js.map